<template>
  <div class="space-y-4 rounded-lg border border-hazel-200 p-2">
    <ListItem :has-error="hasError" @click="showModal = true">
      <ListItemColumn :label="t('maxQuantity')">
        {{ priceRange.maxQuantity }}
      </ListItemColumn>
      <ListItemColumn
        :label="
          t(
            t(
              getOptionByValue(1)?.pricingType === VehicleExtraOptionPricingType.FIXED
                ? 'fixPrice'
                : 'pricePerDay',
            ),
          )
        "
        class="whitespace-break-spaces"
      >
        {{ printPrice() }}
      </ListItemColumn>
    </ListItem>
  </div>

  <Modal v-model="showModal" content-class="!overflow-y-visible">
    <OptionForm
      :is-quantity-option="true"
      :price-range-index="priceRangeIndex"
      :option-index="1"
      :extra-type="extraType"
      @close="showModal = false"
    />
  </Modal>
</template>

<script setup lang="ts">
import type { ArrayType } from '@/utils/array-type';
import {
  type EditAutoAssignVehicleExtraDto,
  VehicleExtraOptionPricingType,
} from '@carvia/ros-client-types';
import { useField } from 'vee-validate';
import type { EditExtraDto } from './EditExtrasView.vue';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import ListItemColumn from '../ui/ListItemColumn.vue';
import ListItem from '@/components/ListItem.vue';
import { formatCurrency } from '@/utils/format-numbers';
import OptionForm from './OptionForm.vue';
import Modal from '@/components/Modal.vue';

const props = defineProps<{
  priceRangeIndex: number;
  extraType: EditAutoAssignVehicleExtraDto['type'];
  hasError?: boolean;
}>();

const { t } = useI18n();

const showModal = ref(false);

const { value: priceRange, setValue: setPriceRange } = useField<
  ArrayType<EditExtraDto['priceRanges']>
>('priceRanges.' + props.priceRangeIndex);

const getOptionByValue = (value: number) => {
  return priceRange.value.options.find((opt) => parseInt(opt.value) === value);
};

const printPrice = () => {
  const optionWithValue1 = getOptionByValue(1)!;
  if (optionWithValue1.pricingType === VehicleExtraOptionPricingType.FIXED) {
    return formatCurrency(optionWithValue1.price ?? 0);
  }
  const priceLines =
    optionWithValue1?.pricePerDay?.map((price) =>
      t('pricePerDayValues', { count: price.fromDay, price: formatCurrency(price.pricePerDay) }),
    ) ?? [];
  return priceLines?.length ? priceLines.join('\n') : t('noPrices');
};
</script>

<i18n lang="json">
{
  "en": {
    "maxQuantity": "Max quantity",
    "fixPrice": "Fix price",
    "pricePerDay": "Price per day",
    "pricePerDayValues": "From {count} days: {price}/day | From {count} day: {price}/day",
    "noPrices": "No prices"
  },
  "de": {
    "maxQuantity": "Max. Anzahl",
    "fixPrice": "Pauschalpreis",
    "pricePerDay": "Preis pro Tag",
    "pricePerDayValues": "Ab {count} Tag: {price}/Tag | Ab {count} Tagen: {price}/Tag",
    "noPrices": "Keine Preise"
  },
  "es": {
    "maxQuantity": "Cantidad máx.",
    "fixPrice": "Precio fijo",
    "pricePerDay": "Precio por día",
    "pricePerDayValues": "Desde {count} día: {price}/día | Desde {count} días: {price}/día",
    "noPrices": "Sin precios"
  }
}
</i18n>
